import React, { useCallback, useMemo } from 'react';
import { CompositeDataSetCommonModels } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetCommonModelValidation } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetCommonModelValidationRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { DataSetCompositeReportsConfigComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeReportsConfigComponent';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useCrudOperations } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-crud-operations';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Button } from 'apps/sigma2-ui/src/components/ui/Button';
import { Table } from 'apps/sigma2-ui/src/components/ui/Table';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { DataSetCompositeReportsFinalReviewComponent } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeReportsFinalReviewComponent';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { UseGenerateReportsInsightsPayload } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/use-generate-reports-insights';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';

const ValidateCommonModelConfigStep = z.enum([
  'reports',
  'validations',
  'final-review',
  'complete',
]);

type ValidateCommonModelConfigStep = z.infer<
  typeof ValidateCommonModelConfigStep
>;

export const DataSetCompositeRecipeValidateCommonModelConfigComponent = ({
  config,
  setConfig,
  insightsPayload,
}: {
  config: CompositeDataSetCommonModelValidationRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
  insightsPayload: UseGenerateReportsInsightsPayload;
}) => {
  const language = useLanguage();

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<ValidateCommonModelConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'reports', display: 'Reports' },
        { value: 'validations', display: 'Validations' },
        { value: 'final-review', display: 'Final Review' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const setReportsToValidate = useCallback(
    (reports: CompositeDataSetCommonModelValidation[]) => {
      setConfig({
        ...config,
        reportsToValidate: reports,
      });
    },
    [config, setConfig],
  );

  const crudValidationsPayload = useCrudOperations({
    items: config.reportsToValidate,
    setItems: setReportsToValidate,
  });

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<ValidateCommonModelConfigStep>({
      items: stepItems,
      onNext: () => true,
    });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'reports':
        return (
          <DataSetCompositeReportsConfigComponent
            reports={config.reports}
            setReports={(reports) => {
              setConfig({ ...config, reports });
            }}
            insightsPayload={insightsPayload}
          />
        );
      case 'validations':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <Button
              size={'small'}
              text={'Add Validation'}
              behavior={{
                type: 'onClick',
                onClick: () => {
                  crudValidationsPayload.onAdd({
                    reportName: '',
                    commonModelValidator: 'Applications',
                  });
                },
              }}
            />

            <Table
              columnDefinitions={[
                {
                  id: 'reportName',
                  header: () => <div>Report Name</div>,
                  cell: ({ item, rowIndex }) => (
                    <input
                      className="form-input"
                      value={item.reportName}
                      onChange={(e) => {
                        crudValidationsPayload.onEdit(rowIndex, {
                          reportName: e.target.value,
                        });
                      }}
                    />
                  ),
                },
                {
                  id: 'commonModelValidator',
                  header: () => <div>Common Model Validator</div>,
                  cell: ({ item, rowIndex }) => (
                    <SelectBoxWithSearch
                      type={'single'}
                      setSelected={(selected) => {
                        if (selected === null || typeof selected !== 'string') {
                          return;
                        }

                        crudValidationsPayload.onEdit(rowIndex, {
                          commonModelValidator: selected,
                        });
                      }}
                      selected={item.commonModelValidator}
                      list={CompositeDataSetCommonModels.map((model) => ({
                        optionValue: model,
                        optionLabel: model,
                      }))}
                      label={null}
                    />
                  ),
                },
                {
                  id: 'delete',
                  header: () => <div>Delete</div>,
                  cell: ({ rowIndex }) => (
                    <Button
                      size={'small'}
                      text={'Delete'}
                      behavior={{
                        type: 'onClick',
                        onClick: () => {
                          crudValidationsPayload.onDelete(rowIndex);
                        },
                      }}
                    />
                  ),
                },
              ]}
              items={config.reportsToValidate}
            />
          </Spacing>
        );
      case 'final-review':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <DataSetCompositeReportsFinalReviewComponent
              reports={config.reports}
            />

            <S2Typography
              variant={'headline-5-alt'}
              translatedContent={language.dangerousUserInput({
                value: 'Validations',
              })}
            />

            {config.reportsToValidate.map((report, index) => {
              return (
                <Spacing key={index} size={'md'} direction={'horizontal'}>
                  <Box pl={'md'}>
                    <S2Typography
                      variant={'headline-6-alt'}
                      translatedContent={language.dangerousUserInput({
                        value: `${report.reportName} -> ${report.commonModelValidator}`,
                      })}
                    />
                  </Box>
                </Spacing>
              );
            })}
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [
    dataWizardPayload.selectedItem,
    config,
    insightsPayload,
    language,
    setConfig,
    crudValidationsPayload,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
